/* eslint-disable @typescript-eslint/no-misused-new */
import { FunctionComponent, memo, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { loadScript, loadStyle } from '../../utils';
import { Button, ButtonType } from '../Button';

import classNames from 'classnames';
import classes from './JSONEditor.module.scss';

interface ValidateError {
  line: number;
  message: string;
  type: 'error';
}

interface Editor {
  validate(): Promise<ValidateError[]>;
  set(json: object): void;
  setSchema(schema: object): void;
  get(): object;
  new (container: HTMLElement, config: unknown): Editor;
}

declare global {
  interface Window {
    JSONEditor: Editor;
  }
}

interface JSONEditorProps {
  id: string;
  schemaUrl: string;
  configUrl: string;
  className?: string;
}

export const JSONEditor: FunctionComponent<JSONEditorProps> = memo(
  ({ id, schemaUrl, configUrl, className }) => {
    const { t } = useTranslation();

    const [editor, setEditor] = useState<Editor | null>(null);

    const [validateErrors, setValidateErrors] = useState<ValidateError[]>([]);

    const init = useCallback(async () => {
      await loadScript({
        src: 'https://cdnjs.cloudflare.com/ajax/libs/jsoneditor/9.10.2/jsoneditor.min.js',
      });

      await loadStyle({
        href: 'https://cdnjs.cloudflare.com/ajax/libs/jsoneditor/9.10.2/jsoneditor.min.css',
      });

      const container = document.getElementById(id);

      if (!container) {
        console.error('Can not find container for JSON editor by id', id);
        return;
      }

      const options = {
        mode: 'code',
        allowSchemaSuggestions: true,
        onError: (error: Error) => console.error(error),
        onValidationError: (errors: ValidateError[]) => setValidateErrors(errors),
      };

      setEditor(new window.JSONEditor(container, options));
    }, [id]);

    useEffect(() => {
      init();
    }, [init]);

    useEffect(() => {
      if (!editor) {
        return;
      }

      const loadSchema = async () => {
        const schema = await fetch(schemaUrl, {
          credentials: 'include',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        }).then((result) => result.json());

        editor.setSchema(schema);
      };

      loadSchema();
    }, [editor, schemaUrl]);

    useEffect(() => {
      if (!editor) {
        return;
      }

      const loadConfig = async () => {
        const config = await fetch(configUrl, {
          credentials: 'include',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        }).then((result) => result.json());

        editor.set(config);
      };

      loadConfig();
    }, [editor, configUrl]);

    const onSave = useCallback(async () => {
      if (!editor) {
        return;
      }

      const data = await fetch(configUrl, {
        method: 'PUT',
        credentials: 'include',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify(editor.get()),
      }).then((result) => result.json());

      if (Boolean(data?.errors?.length)) {
        toast.error(data.errors[0].message);
        return;
      }

      editor.set(data);
    }, [editor, configUrl]);

    return (
      <div className={classes['container']}>
        <div id={id} className={classNames(classes['editor'], className)} />
        <Button
          type={ButtonType.primary}
          label={t('common.save')}
          onClick={onSave}
          disabled={Boolean(validateErrors.length)}
        />
      </div>
    );
  }
);
